/* Copyright Information: Created by BuyGroup */
import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, CardBody, Button, Spinner } from "reactstrap";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../components/common/breadcrumb";
import BootstrapTable from "react-bootstrap-table-next";
import Moment from "react-moment";
import "./Quotes.css";
import { formatPhoneNumber, formatAddress } from "../utils";
import { assignSuppliersToQuote, viewQuote } from "../services/QuoteServices";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssignSupplierModal from "./AssignSupplierModal";
import SupplierQuoteModal from "./SupplierQuoteModal";

// Quote items table columns
const quoteItemsColumns = [
  {
    dataField: "product_name",
    text: "Product",
    headerStyle: {
      width: "40%",
    },
  },
  {
    dataField: "asin",
    text: "ASIN",
    headerStyle: {
      width: "15%",
    },
  },
  {
    dataField: "quantity",
    text: "Quantity",
    align: "center",
    headerStyle: {
      width: "10%",
    },
  },
  {
    dataField: "notes",
    text: "Notes",
    headerStyle: {
      width: "35%",
    },
  },
];

const notAssignedQuoteStatuses = ["submitted", "sent_to_supplier"];

const QuoteDetails = () => {
  const { quoteId } = useParams();
  const [quoteData, setQuoteData] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [assignedSuppliers, setAssignedSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assignSupplierModal, setAssignSupplierModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewSupplierModal, setViewSupplierModal] = useState(false);
  const [viewingSupplier, setViewingSupplier] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // get quote details
  const getQuoteDetails = useCallback(() => {
    setIsLoading(true);
    viewQuote(quoteId)
      .then((res) => {
        console.log("res", res.data);
        setQuoteData(res.data.quote);
        setQuoteItems(res.data.quoteItems);
        setSuppliers(res.data.suppliers);
        setAssignedSuppliers(res.data.assignedSuppliers);
      })
      .catch((err) => {
        console.error("error in get quote details ", err);
        toast.error("Failed to get quote details");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [quoteId]);

  useEffect(() => {
    getQuoteDetails();
  }, [getQuoteDetails]);

  // Handle opening assign supplier modal
  const handleOpenAssignSupplierModal = () => {
    setAssignSupplierModal(true);
  };

  // Handle closing assign supplier modal
  const handleCloseAssignSupplierModal = () => {
    setAssignSupplierModal(false);
  };

  // Handle submit assign suppliers
  const handleSubmitAssignSuppliers = (selectedSuppliers) => {
    if (selectedSuppliers.length === 0) {
      toast.error("Please select at least one supplier");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      id: quoteData.id,
      suppliers: selectedSuppliers,
    };

    assignSuppliersToQuote(quoteId, payload)
      .then((res) => {
        toast.success(res.message);
        handleCloseAssignSupplierModal();
        getQuoteDetails(); // Refresh quote details
      })
      .catch((err) => {
        console.error("error in assign suppliers to quote ", err);
        toast.error("Failed to assign suppliers to quote");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Handle opening supplier modal in edit mode
  const handleOpenEditSupplierModal = (supplier) => {
    setIsEditMode(true);
    setViewingSupplier(supplier);
    setViewSupplierModal(true);
  };

  // Handle opening supplier modal in view mode
  const handleOpenViewSupplierModal = (supplier) => {
    setIsEditMode(false);
    setViewingSupplier(supplier);
    setViewSupplierModal(true);
  };

  // Handle closing view supplier modal
  const handleCloseViewSupplierModal = () => {
    setViewSupplierModal(false);
    setViewingSupplier(null);
    setIsEditMode(false);
  };

  // Handle view assigned quote
  const handleViewAssignedQuote = () => {
    const [selectedSupplier] = assignedSuppliers.filter(
      (supplier) => supplier.is_quote_assigned === true
    );
    setViewingSupplier(selectedSupplier);
    setIsEditMode(false);
    setViewSupplierModal(true);
  };

  if (isLoading) {
    return (
      <>
        <Breadcrumbs
          title="Quote Detail"
          parent="Get a Quote"
          subtitle={`Quote ${quoteId}`}
        />
        <div className="container-fluid">
          <div className="custom-loader">
            <Spinner animation="border" />
          </div>
        </div>
      </>
    );
  }

  if (!quoteData) {
    return (
      <>
        <Breadcrumbs
          title="Quote Detail"
          parent="Get a Quote"
          subtitle={`Quote ${quoteId}`}
        />
        <div className="container-fluid">
          <Card className="card-box">
            <CardBody>
              <p>Quote not found</p>
            </CardBody>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs
        title="Quote Detail"
        parent="Get a Quote"
        subtitle={`Quote ${quoteData.quote_id}`}
      />
      <div className="container-fluid">
        <Row className="row">
          <Col md="12">
            {/* Quote Header Card */}
            <Card className="card-box mb-4">
              <CardBody>
                <Row className="align-items-center mb-4">
                  <Col md="6">
                    <h4 className="mb-0 text-none">
                      <strong>Quote: {quoteData.quote_id}</strong>
                    </h4>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <strong className="mr-2">CUSTOMER:</strong>
                      {quoteData.name}
                    </div>
                    <div className="mb-3">
                      <strong className="mr-2">EMAIL:</strong>
                      {quoteData.email}
                    </div>
                    <div className="mb-3">
                      <strong className="mr-2">SHIPPING ADDRESS:</strong>
                      {quoteData?.address2 && quoteData.address2 + ", "}
                      {formatAddress(
                        quoteData.street,
                        quoteData.city,
                        quoteData.state,
                        quoteData.zip_code
                      )}
                    </div>
                    <div className="mb-3">
                      <strong className="mr-2">ASSIGNED QUOTE:</strong>
                      {notAssignedQuoteStatuses.includes(quoteData.status) ? (
                        <span className="text-muted">Awaiting Quote</span>
                      ) : (
                        <Button
                          color="primary"
                          size="sm"
                          onClick={handleViewAssignedQuote}
                        >
                          View Quote
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <strong className="mr-2">PHONE:</strong>
                      {formatPhoneNumber(quoteData.phone_number)}
                    </div>
                    <div className="mb-3">
                      <strong className="mr-2">DATE:</strong>
                      <Moment format="MM/DD/YYYY">
                        {quoteData.start_date}
                      </Moment>
                    </div>
                    <div className="mb-3">
                      <strong className="mr-2">EXPIRY DATE:</strong>
                      <Moment format="MM/DD/YYYY">
                        {quoteData.expiry_date}
                      </Moment>
                    </div>
                    <div className="mb-3">
                      <strong className="mr-2">REQUESTED DELIVERY DATE:</strong>
                      <Moment format="MM/DD/YYYY">
                        {quoteData.request_delivery_date}
                      </Moment>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Row>
              {/* Quote Items Card */}
              <Col md="8">
                <Card className="card-box">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5>Quote Items</h5>
                  </div>
                  <CardBody>
                    {quoteItems && quoteItems.length > 0 ? (
                      <>
                        <BootstrapTable
                          keyField="id"
                          data={quoteItems}
                          columns={quoteItemsColumns}
                          striped
                          hover
                          condensed
                          bordered={false}
                          noDataIndication={"No items found"}
                          rowClasses="row-hover"
                        />
                      </>
                    ) : (
                      <p>No items found</p>
                    )}
                  </CardBody>
                </Card>
              </Col>

              {/* Supplier Quotes Card */}
              <Col md="4">
                <Card className="card-box">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5>Supplier Quotes</h5>
                    {quoteData.status === "submitted" && (
                      <Button
                        color="primary"
                        size="sm"
                        className="assign-supplier-btn"
                        onClick={handleOpenAssignSupplierModal}
                      >
                        Assign Supplier
                      </Button>
                    )}
                  </div>
                  <CardBody>
                    {assignedSuppliers && assignedSuppliers.length > 0 ? (
                      <div className="selected-suppliers-list">
                        {assignedSuppliers.map((supplier) => (
                          <div
                            key={supplier.id}
                            className="selected-supplier-item"
                          >
                            <div className="supplier-info">
                              <div className="supplier-name">
                                {supplier.company_name}
                              </div>
                              {/* {quoteData.status !== "sent_to_supplier" && (
                                <div className="supplier-email">
                                  Expires:&nbsp;
                                  <Moment format="MM/DD/YYYY">
                                    {quoteData.valid_date ||
                                      quoteData.expiry_date}
                                  </Moment>
                                </div>
                              )} */}
                            </div>
                            <div className="selected-supplier-actions">
                              {quoteData.status === "sent_to_supplier" &&
                                supplier.company_name !== "Amazon Supplier" && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() =>
                                      handleOpenEditSupplierModal(supplier)
                                    }
                                  >
                                    Add
                                  </Button>
                                )}
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() =>
                                  handleOpenViewSupplierModal(supplier)
                                }
                              >
                                View
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">
                        No supplier quotes available.
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Assign Supplier Modal */}
      <AssignSupplierModal
        isOpen={assignSupplierModal}
        toggle={handleCloseAssignSupplierModal}
        suppliers={suppliers}
        onSubmit={handleSubmitAssignSuppliers}
        isSubmitting={isSubmitting}
      />

      {/* View/Edit Supplier Quote Modal */}
      <SupplierQuoteModal
        isOpen={viewSupplierModal}
        toggle={handleCloseViewSupplierModal}
        viewingSupplier={viewingSupplier}
        quoteData={quoteData}
        quoteItems={quoteItems}
        isEditMode={isEditMode}
        quoteId={quoteId}
        onSaveSuccess={getQuoteDetails}
      />
    </>
  );
};

export default QuoteDetails;
/* Copyright Information: Created by BuyGroup */
